import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  Avatar,
  Paper,
  InputAdornment,
} from '@mui/material';
import { Visibility, Search } from '@mui/icons-material';
import { adminApi } from '../../services/adminApi';
import {
  GlassCard,
  SectionTitle,
  TableWrapper,
  StyledTableContainer,
  StyledTableHead,
  StyledTableRow,
  ActionIconButton,
} from './AdminStyles';

interface Conversation {
  id: number;
  user_id: number;
  title: string;
  created_at: string;
  updated_at: string;
}

interface Message {
  id: number;
  conversation_id: number;
  sender: 'user' | 'bot';
  content: string;
  timestamp: string;
  media_attachment_url?: string;
}

const ConversationReview: React.FC = () => {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const [openMessageDialog, setOpenMessageDialog] = useState<boolean>(false);
  const [selectedConversationMessages, setSelectedConversationMessages] = useState<Message[]>([]);
  const [selectedConversationTitle, setSelectedConversationTitle] = useState<string>('');
  const [messagesLoading, setMessagesLoading] = useState<boolean>(false);
  const [messagesError, setMessagesError] = useState<string | null>(null);

  const fetchConversations = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await adminApi.getConversations({ search: searchTerm });
      setConversations(data);
    } catch (err) {
      setError('Failed to fetch conversations.');
      console.error('Error fetching conversations:', err);
    } finally {
      setLoading(false);
    }
  }, [searchTerm]);

  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  const fetchConversationMessages = async (conversationId: number) => {
    setMessagesLoading(true);
    setMessagesError(null);
    try {
      const data = await adminApi.getConversationMessages(conversationId);
      setSelectedConversationMessages(data);
    } catch (err) {
      setMessagesError('Failed to fetch conversation messages.');
      console.error('Error fetching conversation messages:', err);
    } finally {
      setMessagesLoading(false);
    }
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const handleSearchSubmit = () => {
    fetchConversations();
  };

  const handleViewConversationDetails = (conversationId: number, conversationTitle: string) => {
    setSelectedConversationTitle(conversationTitle);
    setOpenMessageDialog(true);
    fetchConversationMessages(conversationId);
  };

  const handleCloseMessageDialog = () => {
    setOpenMessageDialog(false);
    setSelectedConversationMessages([]);
    setSelectedConversationTitle('');
  };

  if (loading && conversations.length === 0) {
    return <Typography>Nalaganje pogovorov...</Typography>;
  }

  if (error) {
    return <Typography color="error">{error}</Typography>;
  }

  return (
    <Box>
      <GlassCard elevation={0}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 3,
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <SectionTitle>Pregledovanje Pogovorov</SectionTitle>
          <TextField
            placeholder="Išči pogovore..."
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={handleSearchChange}
            onKeyPress={(e) => {
              if (e.key === 'Enter') handleSearchSubmit();
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '12px',
                backgroundColor: '#f9fafb',
                '& fieldset': { borderColor: '#e5e7eb' },
                '&:hover fieldset': { borderColor: '#6366f1' },
                '&.Mui-focused fieldset': { borderColor: '#6366f1' },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ color: '#9ca3af' }} />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <TableWrapper>
          <StyledTableContainer>
            <Table sx={{ minWidth: 650 }} aria-label="conversation review table">
              <StyledTableHead>
                <StyledTableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Naslov</TableCell>
                  <TableCell>Uporabnik ID</TableCell>
                  <TableCell>Ustvarjeno</TableCell>
                  <TableCell>Posodobljeno</TableCell>
                  <TableCell align="right">Akcije</TableCell>
                </StyledTableRow>
              </StyledTableHead>
              <TableBody>
                {conversations.map((conversation) => (
                  <StyledTableRow key={conversation.id}>
                    <TableCell
                      component="th"
                      scope="row"
                      sx={{ fontWeight: 'bold', color: '#6366f1 !important' }}
                    >
                      #{conversation.id}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {conversation.title || `Pogovor ${conversation.id}`}
                    </TableCell>
                    <TableCell>{conversation.user_id}</TableCell>
                    <TableCell>{new Date(conversation.created_at).toLocaleString()}</TableCell>
                    <TableCell>{new Date(conversation.updated_at).toLocaleString()}</TableCell>
                    <TableCell align="right">
                      <ActionIconButton
                        onClick={() =>
                          handleViewConversationDetails(
                            conversation.id,
                            conversation.title || `Pogovor ${conversation.id}`
                          )
                        }
                        title="Poglej podrobnosti"
                      >
                        <Visibility fontSize="small" />
                      </ActionIconButton>
                    </TableCell>
                  </StyledTableRow>
                ))}
              </TableBody>
            </Table>
          </StyledTableContainer>
        </TableWrapper>
      </GlassCard>

      <Dialog
        open={openMessageDialog}
        onClose={handleCloseMessageDialog}
        PaperProps={{ style: { borderRadius: 20, padding: 10 } }}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Pogovor: {selectedConversationTitle}</DialogTitle>
        <DialogContent dividers>
          {messagesLoading ? (
            <Typography>Nalaganje sporočil...</Typography>
          ) : messagesError ? (
            <Typography color="error">{messagesError}</Typography>
          ) : selectedConversationMessages.length === 0 ? (
            <Typography>Ni najdenih sporočil za ta pogovor.</Typography>
          ) : (
            <List>
              {selectedConversationMessages.map((message) => (
                <ListItem
                  key={message.id}
                  sx={{
                    justifyContent: message.sender === 'user' ? 'flex-end' : 'flex-start',
                    mb: 1,
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      flexDirection: message.sender === 'user' ? 'row-reverse' : 'row',
                      maxWidth: '80%',
                    }}
                  >
                    <Avatar
                      sx={{
                        bgcolor: message.sender === 'user' ? 'primary.main' : 'secondary.main',
                        ml: message.sender === 'user' ? 1 : 0,
                        mr: message.sender === 'bot' ? 1 : 0,
                      }}
                    >
                      {message.sender === 'user' ? 'U' : 'B'}
                    </Avatar>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 1.5,
                        borderRadius: '20px',
                        borderTopLeftRadius: message.sender === 'user' ? '20px' : '5px',
                        borderTopRightRadius: message.sender === 'user' ? '5px' : '20px',
                        bgcolor: message.sender === 'user' ? 'primary.light' : 'grey.200',
                        color: message.sender === 'user' ? 'primary.contrastText' : 'text.primary',
                      }}
                    >
                      <Typography variant="body2">{message.content}</Typography>
                      {message.media_attachment_url && (
                        <Box mt={1}>
                          <img
                            src={message.media_attachment_url}
                            alt="Media attachment"
                            style={{ maxWidth: '100%', height: 'auto', borderRadius: '8px' }}
                          />
                        </Box>
                      )}
                      <Typography
                        variant="caption"
                        display="block"
                        sx={{
                          mt: 0.5,
                          textAlign: message.sender === 'user' ? 'right' : 'left',
                          color:
                            message.sender === 'user' ? 'rgba(255,255,255,0.7)' : 'text.secondary',
                        }}
                      >
                        {new Date(message.timestamp).toLocaleTimeString()}
                      </Typography>
                    </Paper>
                  </Box>
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseMessageDialog}
            sx={{ borderRadius: '10px', color: '#6b7280' }}
          >
            Zapri
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ConversationReview;
